import React, { useState, useEffect } from "react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { Award, Edit, Eye, Trash2, MapPin, Folder } from "lucide-react";
import ValidateExpertButton from "./ValidateExpertButton";
import "./SharedAccessSection.css";
import { useTranslation } from "react-i18next";

const LOC_COPY = {
  fr: { label: "Accès aux emplacements", hint: "Autorise l'expert à voir vos lieux de conservation et à réaliser une visite d'entrepôt.", on: "Autorisé", off: "Non autorisé" },
  en: { label: "Location access", hint: "Lets the expert see your storage places and run a warehouse visit.", on: "Allowed", off: "Not allowed" },
  de: { label: "Standortzugriff", hint: "Erlaubt dem Experten, Ihre Aufbewahrungsorte zu sehen und eine Lagerbegehung durchzuführen.", on: "Erlaubt", off: "Nicht erlaubt" },
};
const AUTH_COPY = {
  fr: { title: "Autorisations accordées à ce tiers", view: "Consultation", edit: "Modification", on: "Oui", off: "Non", editHint: "Le tiers peut modifier les fiches de cette collection." },
  en: { title: "Authorizations granted to this third party", view: "View", edit: "Edit", on: "Yes", off: "No", editHint: "The third party can edit this collection's records." },
  de: { title: "Diesem Dritten erteilte Berechtigungen", view: "Ansicht", edit: "Bearbeiten", on: "Ja", off: "Nein", editHint: "Der Dritte kann die Datensätze dieser Sammlung bearbeiten." },
};
const pickLang = (i18n) => String(i18n.resolvedLanguage || i18n.language || "fr").slice(0, 2).toLowerCase();
const INFO_COPY = {
  fr: "Les accès s'accordent depuis chaque collection : ouvrez une collection pour partager (ou retirer) l'accès à un tiers. Ici, vous voyez et ajustez les autorisations déjà accordées.",
  en: "Access is granted from each collection: open a collection to share (or remove) access for a third party. Here, you review and adjust the authorizations already granted.",
  de: "Zugriffe werden aus jeder Sammlung heraus erteilt: Öffnen Sie eine Sammlung, um einem Dritten Zugriff zu gewähren (oder zu entziehen). Hier prüfen und ändern Sie bereits erteilte Berechtigungen.",
};

function SharedAccessSection({ collectionId }) {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();

  const [experts, setExperts] = useState([]);
  const [familyOfficeUsers, setFamilyOfficeUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingPermId, setUpdatingPermId] = useState(null);

  useEffect(() => {
    if (user?.id) loadSharedAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, collectionId]);

  const safeStr = (v) => (typeof v === "string" ? v.trim() : "");

  const loadSharedAccess = async () => {
    if (!user?.id) {
      console.warn("[shared-access] Skipped: user.id is missing");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // ── 1. Permissions depuis la vue enrichie ──────────────────────────
      let query = supabase
        .from("collection_permissions_enriched")
        .select(
          [
            "id",
            "grantee_id",
            "collection_id",
            "can_view",
            "can_edit",
            "can_view_location",
            "created_at",
            "grantee_name",
            "grantee_role",
            "grantee_plan",
            "grantee_company",
          ].join(",")
        )
        .eq("client_id", user.id)
        .eq("can_view", true);

      if (collectionId) {
        query = query.eq("collection_id", collectionId);
      }

      const { data: permissions, error: permError } = await query.order(
        "created_at",
        { ascending: false }
      );

      if (permError) throw permError;

      if (!permissions || permissions.length === 0) {
        setExperts([]);
        setFamilyOfficeUsers([]);
        return;
      }

      // ── 2. Titres des collections ──────────────────────────────────────
      const collectionIds = permissions.map((p) => p.collection_id).filter(Boolean);
      let collectionsData = [];
      const hasNullCollections = permissions.some((p) => p.collection_id === null);

      if (collectionIds.length > 0) {
        const { data, error: colError } = await supabase
          .from("collections")
          .select("id, title")
          .in("id", collectionIds);
        if (colError) console.warn("[shared-access] collections error:", colError);
        collectionsData = data || [];
      }

      if (hasNullCollections) {
        const { data: allColls, error: collError } = await supabase
          .from("collections")
          .select("id, title")
          .eq("client_id", user.id);
        if (collError) console.warn("[shared-access] collections(null) error:", collError);
        const existing = new Set(collectionsData.map((c) => c.id));
        (allColls || []).forEach((c) => {
          if (!existing.has(c.id)) collectionsData.push(c);
        });
      }

      // ── 3. Fiche répertoire experts (optionnelle) ──────────────────────
      // On cherche les grantees dans la table experts (répertoire Artkiv).
      // Si absent → expert.id = null → ValidateExpertButton se désactive.
      const granteeIds = [
        ...new Set(permissions.map((p) => p.grantee_id).filter(Boolean)),
      ];

      const { data: expertsRepertoire, error: expError } = await supabase
        .from("experts")
        .select("id, client_id, name, specialty, image_url, certified, organization, location")
        .in("client_id", granteeIds);

      if (expError) console.warn("[shared-access] experts table error:", expError);

      // Map client_id → fiche répertoire (null si absent)
      const repertoireByClientId = Object.fromEntries(
        (expertsRepertoire || []).map((e) => [e.client_id, e])
      );

      // ── 4. Classification : expert via grantee_role (pas via table experts) ──
      // Correction clé : on ne dépend plus de la présence dans la table experts
      // pour décider qui est expert. On utilise le rôle du profil.
      const isExpertRole = (role) =>
        ["expert", "art_advisor", "art_advisor_premium"].includes(role);

      const pushCollection = (target, perm) => {
        if (perm.collection_id) {
          const col = collectionsData.find((c) => c.id === perm.collection_id);
          if (col && !target.collections.some((c) => c.id === col.id)) {
            target.collections.push({ id: col.id, title: col.title || "" });
          }
        } else {
          collectionsData.forEach((col) => {
            if (!target.collections.some((c) => c.id === col.id)) {
              target.collections.push({ id: col.id, title: col.title || "" });
            }
          });
        }
      };

      const expertsGrouped = {};
      const usersGrouped = {};

      permissions.forEach((perm) => {
        const gid = perm.grantee_id;
        const role = safeStr(perm.grantee_role) || "unknown";
        const nameFromView = safeStr(perm.grantee_name) || safeStr(perm.grantee_company) || "";
        const company = safeStr(perm.grantee_company) || null;
        const plan = safeStr(perm.grantee_plan) || "unknown";
        const fiche = repertoireByClientId[gid] || null;

        if (isExpertRole(role)) {
          // ─ Groupe Experts ─
          if (!expertsGrouped[gid]) {
            expertsGrouped[gid] = {
              id: fiche?.id || null,          // id TABLE experts (null si non répertorié)
              client_id: gid,                  // vrai user id
              name: fiche?.name || nameFromView,
              specialty: fiche?.specialty || null,
              image_url: fiche?.image_url || null,
              certified: fiche?.certified || false,
              organization: fiche?.organization || company || null,
              role,
              permissions: [],
              collections: [],
              can_edit: false,
              can_view_location: false,
              type: "expert",
              created_at: perm.created_at,
            };
          }
          expertsGrouped[gid].permissions.push(perm);
          expertsGrouped[gid].can_edit = expertsGrouped[gid].can_edit || perm.can_edit;
          expertsGrouped[gid].can_view_location = expertsGrouped[gid].can_view_location || perm.can_view_location;
          pushCollection(expertsGrouped[gid], perm);
        } else {
          // ─ Groupe Autres utilisateurs ─
          if (!usersGrouped[gid]) {
            usersGrouped[gid] = {
              id: gid,
              name: nameFromView,
              role,
              plan,
              company,
              permissions: [],
              collections: [],
              can_edit: false,
              type:
                role === "family_office" || role === "user_familyoffice"
                  ? "family_office"
                  : "user",
              created_at: perm.created_at,
            };
          }
          usersGrouped[gid].permissions.push(perm);
          usersGrouped[gid].can_edit = usersGrouped[gid].can_edit || perm.can_edit;
          pushCollection(usersGrouped[gid], perm);
        }
      });

      setExperts(Object.values(expertsGrouped));
      setFamilyOfficeUsers(Object.values(usersGrouped));
    } catch (e) {
      console.error("[shared-access] load error:", e);
      setError(t("sharedAccessManager.errors.load"));
    } finally {
      setLoading(false);
    }
  };

  const revokeAccess = async (idOrPermissionId, _name, type) => {
    if (!user?.id) return;
    try {
      if (type === "permission") {
        const { error } = await supabase
          .from("collection_permissions")
          .delete()
          .eq("id", idOrPermissionId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("collection_permissions")
          .delete()
          .eq("client_id", user.id)
          .eq("grantee_id", idOrPermissionId);
        if (error) throw error;
      }
      await loadSharedAccess();
    } catch (e) {
      console.error("[shared-access] revoke error:", e);
      alert(t("sharedAccessManager.errors.revoke"));
    }
  };

  const togglePermissionEdit = async (permissionId, currentCanEdit) => {
    if (!user?.id) return;
    setUpdatingPermId(permissionId);
    try {
      const { error } = await supabase
        .from("collection_permissions")
        .update({ can_edit: !currentCanEdit })
        .eq("id", permissionId)
        .eq("client_id", user.id);
      if (error) throw error;
      await loadSharedAccess();
    } catch (e) {
      console.error("[shared-access] toggle edit error:", e);
      alert(t("sharedAccessManager.errors.update"));
    } finally {
      setUpdatingPermId(null);
    }
  };

  // Visibilité des emplacements (§8ter opt-in) : nécessaire pour la « visite
  // d'entrepôt » de l'expert. On bascule TOUTES les permissions de ce grantee.
  const toggleLocationAccess = async (granteeUserId, currentCanViewLocation) => {
    if (!user?.id) return;
    setUpdatingPermId("loc-" + granteeUserId);
    try {
      const { error } = await supabase
        .from("collection_permissions")
        .update({ can_view_location: !currentCanViewLocation })
        .eq("client_id", user.id)
        .eq("grantee_id", granteeUserId);
      if (error) throw error;
      await loadSharedAccess();
    } catch (e) {
      console.error("[shared-access] toggle location error:", e);
      alert(t("sharedAccessManager.errors.update"));
    } finally {
      setUpdatingPermId(null);
    }
  };

  return (
    <div className="shared-access-section">
      <div className="shared-access-header">
        <div>
          <h3>{t("sharedAccessManager.title")}</h3>
          <p className="shared-access-stats">
            {t("sharedAccessManager.stats", { experts: experts.length, users: familyOfficeUsers.length })}
          </p>
        </div>
      </div>

      {/* Rappel : les accès se donnent au niveau des collections (évite la confusion) */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, margin: "4px 0 14px", padding: "10px 12px", border: "1px solid #ece7df", borderRadius: 10, background: "#faf9f6", fontSize: 12.5, lineHeight: 1.45, color: "#6b6b6b" }}>
        <Folder size={15} color="#8B7355" style={{ marginTop: 1, flexShrink: 0 }} />
        <span>{INFO_COPY[pickLang(i18n)] || INFO_COPY.fr}</span>
      </div>

      {loading && <div className="shared-access-loading">{t("sharedAccessManager.loading")}</div>}

      {error && <div className="shared-access-error">{error}</div>}

      {!loading && !error && experts.length === 0 && familyOfficeUsers.length === 0 && (
        <div className="shared-access-empty">
          {t("sharedAccessManager.empty")}
        </div>
      )}

      {!loading && experts.length > 0 && (
        <div className="shared-section">
          <h4 className="section-title">{t("sharedAccessManager.sections.experts", { count: experts.length })}</h4>
          {experts.map((expert) => (
            <ExpertCard
              key={expert.client_id}
              expert={expert}
              onRevoke={revokeAccess}
              onToggleEdit={togglePermissionEdit}
              onToggleLocation={toggleLocationAccess}
              updatingPermId={updatingPermId}
              onValidationChange={loadSharedAccess}
            />
          ))}
        </div>
      )}

      {!loading && familyOfficeUsers.length > 0 && (
        <div className="shared-section">
          <h4 className="section-title">
            {t("sharedAccessManager.sections.users", { count: familyOfficeUsers.length })}
          </h4>
          {familyOfficeUsers.map((u) => (
            <UserCard
              key={u.id}
              user={u}
              onRevoke={revokeAccess}
              onToggleEdit={togglePermissionEdit}
              updatingPermId={updatingPermId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ExpertCard — identique à l'original
// expert.id = id dans table experts (null si non répertorié)
// expert.client_id = vrai user id
// ─────────────────────────────────────────────────────────────
function ExpertCard({ expert, onRevoke, onToggleEdit, onToggleLocation, updatingPermId, onValidationChange }) {
  const { t, i18n } = useTranslation();
  const expertName = expert.name || t("sharedAccessManager.fallback.expert");
  const lc = LOC_COPY[pickLang(i18n)] || LOC_COPY.fr;
  const ac = AUTH_COPY[pickLang(i18n)] || AUTH_COPY.fr;
  const locBusy = updatingPermId === "loc-" + expert.client_id;
  return (
    <div className="expert-card">
      <div className="expert-card-header">
        <div className="expert-avatar">
          {expert.image_url ? (
            <img src={expert.image_url} alt={expert.name} />
          ) : (
            <div className="expert-avatar-placeholder">
              {expertName.charAt(0).toUpperCase() || "E"}
            </div>
          )}
        </div>

        <div className="expert-info">
          <div className="expert-name-row">
            <div className="expert-name">{expertName}</div>
            {expert.certified && (
              <div className="certified-badge">
                <Award size={12} />
                {t("sharedAccessManager.certified")}
              </div>
            )}
          </div>
          <div className="expert-specialty">
            {expert.specialty || t("sharedAccessManager.fallback.expert")}
            {expert.organization && ` • ${expert.organization}`}
          </div>
        </div>

        {/* expertId = null si pas dans le répertoire → ValidateExpertButton se désactive */}
        <ValidateExpertButton
          expertId={expert.id}
          expertName={expertName}
          onValidationChange={onValidationChange}
        />
      </div>

      {/* §8ter — visibilité des emplacements (requise pour la visite d'entrepôt) */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, margin: "10px 0", padding: "10px 12px", border: "1px solid #ece7df", borderRadius: 10, background: "#faf9f6" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8, minWidth: 0 }}>
          <MapPin size={16} color="#8B7355" style={{ marginTop: 1, flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#1A1A1A" }}>{lc.label}</div>
            <div style={{ fontSize: 12, color: "#8a8578", lineHeight: 1.35 }}>{lc.hint}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => onToggleLocation?.(expert.client_id, expert.can_view_location)}
          disabled={locBusy}
          aria-pressed={!!expert.can_view_location}
          title={expert.can_view_location ? lc.on : lc.off}
          style={{
            flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 6,
            padding: "6px 12px", borderRadius: 999, cursor: locBusy ? "default" : "pointer",
            fontSize: 12, fontWeight: 600, opacity: locBusy ? 0.6 : 1,
            border: "1px solid " + (expert.can_view_location ? "#C9A961" : "#d8d3c8"),
            background: expert.can_view_location ? "#C9A961" : "#fff",
            color: expert.can_view_location ? "#1A1A1A" : "#8a8578",
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: 999, background: expert.can_view_location ? "#1A1A1A" : "#c4bfb4" }} />
          {locBusy ? "…" : (expert.can_view_location ? lc.on : lc.off)}
        </button>
      </div>

      {expert.permissions && expert.permissions.length > 0 && (
        <div className="collections-section">
          <div className="collections-title">{ac.title}</div>
          <div className="collections-grid">
            {expert.permissions.map((perm) => {
              const collection = expert.collections?.find((c) => c.id === perm.collection_id);
              const collTitle = collection?.title || t("sharedAccessManager.fallback.allCollections");
              const editBusy = updatingPermId === perm.id;
              return (
                <div key={perm.id} className="collection-item">
                  <div className="collection-main">
                    <span className="collection-icon">📁</span>
                    <div className="collection-details">
                      <div className="collection-title">{collTitle}</div>
                      <div className="collection-date">
                        {t("sharedAccessManager.sharedOn", { date: new Date(perm.created_at).toLocaleDateString(i18n.resolvedLanguage || i18n.language || "fr") })}
                      </div>
                    </div>
                  </div>
                  <div className="collection-actions" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    {/* Consultation — active tant que la collection est partagée */}
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: "#6b6b6b" }} title={ac.view}>
                      <Eye size={13} color="#8B7355" /> {ac.view}: <strong style={{ color: "#2c5f2d" }}>{ac.on}</strong>
                    </span>
                    {/* Modification — interrupteur explicite (plus de bascule ambiguë) */}
                    <button
                      type="button"
                      onClick={() => onToggleEdit?.(perm.id, perm.can_edit)}
                      disabled={editBusy}
                      aria-pressed={!!perm.can_edit}
                      title={ac.editHint}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        padding: "5px 11px", borderRadius: 999, cursor: editBusy ? "default" : "pointer",
                        fontSize: 12, fontWeight: 600, opacity: editBusy ? 0.6 : 1,
                        border: "1px solid " + (perm.can_edit ? "#C9A961" : "#d8d3c8"),
                        background: perm.can_edit ? "#C9A961" : "#fff",
                        color: perm.can_edit ? "#1A1A1A" : "#8a8578",
                      }}
                    >
                      <Edit size={12} /> {ac.edit}: {editBusy ? "…" : (perm.can_edit ? ac.on : ac.off)}
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(t("sharedAccessManager.revoke.collectionConfirm", { name: expertName, collection: collTitle }))) {
                          onRevoke(perm.id, expertName, "permission");
                        }
                      }}
                      className="action-button"
                      title={t("sharedAccessManager.revoke.collectionTitle")}
                    >
                      <Trash2 size={14} color="#dc2626" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// UserCard — identique à l'original
// ─────────────────────────────────────────────────────────────
function UserCard({ user, onRevoke, onToggleEdit, updatingPermId }) {
  const { t, i18n } = useTranslation();
  const userName = user.name || t("sharedAccessManager.fallback.user");
  const initials =
    (userName || "U")
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "U";

  let roleLabel = t("sharedAccessManager.roles.user");
  if (user.role === "family_office" || user.role === "user_familyoffice") {
    roleLabel = t("sharedAccessManager.roles.familyOffice");
  } else if (user.role === "expert") {
    roleLabel = t("sharedAccessManager.roles.expert");
  } else if (user.role === "team") {
    roleLabel = t("sharedAccessManager.roles.team");
  }

  return (
    <div className="user-card">
      <div className="user-card-header">
        <div className="user-avatar">{initials}</div>
        <div className="user-info">
          <div className="user-name-row">
            <div className="user-name">{userName}</div>
            <div className="role-badge">{roleLabel}</div>
          </div>
          <div className="user-company">
            {user.company || t("sharedAccessManager.plan", { plan: user.plan })}
          </div>
        </div>
        <button
          onClick={() => {
            if (window.confirm(t("sharedAccessManager.revoke.allConfirm", { name: userName }))) {
              onRevoke(user.id, userName, "grantee");
            }
          }}
          className="revoke-button"
          title={t("sharedAccessManager.revoke.allTitle")}
        >
          {t("sharedAccessManager.revoke.button")}
        </button>
      </div>

      {user.permissions && user.permissions.length > 0 && (
        <div className="collections-section">
          <div className="collections-title">
            {t("sharedAccessManager.sharedCollections", { count: user.permissions.length })}
          </div>
          <div className="collections-grid">
            {user.permissions.map((perm) => {
              const collection = user.collections?.find((c) => c.id === perm.collection_id);
              const collTitle = collection?.title || t("sharedAccessManager.fallback.allCollections");
              return (
                <div key={perm.id} className="collection-item">
                  <div className="collection-main">
                    <span className="collection-icon">📁</span>
                    <div className="collection-details">
                      <div className="collection-title">{collTitle}</div>
                      <div className="collection-date">
                        {t("sharedAccessManager.sharedOn", { date: new Date(perm.created_at).toLocaleDateString(i18n.resolvedLanguage || i18n.language || "fr") })}
                      </div>
                    </div>
                  </div>
                  <div className="collection-actions">
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm(t("sharedAccessManager.permission.switchConfirm", { name: userName, collection: collTitle }))) {
                          onToggleEdit?.(perm.id, perm.can_edit);
                        }
                      }}
                      disabled={updatingPermId === perm.id}
                      className={`permission-toggle ${perm.can_edit ? "can-edit" : "can-view"}`}
                      title={perm.can_edit ? t("sharedAccessManager.permission.toView") : t("sharedAccessManager.permission.toEdit")}
                    >
                      {perm.can_edit ? (
                        <><Edit size={12} /> {t("sharedAccessManager.permission.edit")}</>
                      ) : (
                        <><Eye size={12} /> {t("sharedAccessManager.permission.view")}</>
                      )}
                      <span style={{ fontSize: 11, opacity: 0.9 }}>
                        {updatingPermId === perm.id ? "…" : "↔"}
                      </span>
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(t("sharedAccessManager.revoke.collectionConfirm", { name: userName, collection: collTitle }))) {
                          onRevoke(perm.id, userName, "permission");
                        }
                      }}
                      className="action-button"
                      title={t("sharedAccessManager.revoke.collectionTitle")}
                    >
                      <Trash2 size={14} color="#dc2626" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default SharedAccessSection;